import React, { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@bynder/localization';
import { Button, InputField, Modal, notify } from '@bynder/design-system';
import { EnrichedComponentCollection } from '../ComponentOverview/types';
import ComponentCollectionService from 'packages/services/ComponentCollectionService';

interface UseComponentModalProps {
    onSaveSuccess?: (name: string) => void;
}

export const useCollectionEditModal = (props: UseComponentModalProps) => {
    const { onSaveSuccess } = props;
    const { translate } = useTranslate();
    const [isOpen, setOpen] = useState(false);
    const [collection, setCollection] = useState<EnrichedComponentCollection | null>(null);
    const [name, setName] = useState(collection?.name || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isModified, setIsModified] = useState(false);

    const inputValidator = useMemo(
        () => ({
            nameLength: name.trim().length > 50,
            nameRequired: name.trim().length === 0,
            sameName: collection ? name.trim() === collection.name : false,
        }),
        [name, collection],
    );

    const isInvalid = useMemo(() => Object.values(inputValidator).some((invalid) => invalid), [inputValidator]);

    const validationMessage = useMemo(() => {
        if (inputValidator.nameRequired) {
            return translate('components.collection.edit.validation.nameRequired');
        }

        if (inputValidator.nameLength) {
            return translate('components.collection.edit.validation.nameLength');
        }

        return '';
    }, [inputValidator, translate]);

    const onOpen = (collection: EnrichedComponentCollection) => {
        if (isOpen) {
            return;
        }

        setCollection(collection);
        setName(collection.name);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setCollection(null);
    };

    const onChange = (value: string) => {
        setName(value);
        setIsModified(true);
    };

    const onSave = async ({ id, name }: { id: number; name: string }) => {
        if (isSaving) {
            return;
        }

        setIsSaving(true);

        try {
            const response = await ComponentCollectionService.editComponentCollection(id, name);

            if (response.status === 200) {
                notify({
                    variant: 'success',
                    title: translate('components.collection.edit.success'),
                });
                onSaveSuccess?.(name);
                onClose();
            } else {
                notify({
                    variant: 'error',
                    title: translate('components.collection.edit.error'),
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        setIsModified(false);
    }, []);

    return {
        renderCollectionEditModal: () => (
            <Modal
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                actionPrimary={
                    <Button
                        variant="primary"
                        onClick={() => collection?.id && onSave({ id: collection.id, name })}
                        isLoading={isSaving}
                        isDisabled={isInvalid || isSaving}
                    >
                        {translate('components.collection.edit.modal.save')}
                    </Button>
                }
                actionSecondary={
                    <Button variant="secondary" onClick={() => setOpen(false)} isDisabled={isSaving}>
                        {translate('modal.cancel.title')}
                    </Button>
                }
                title={translate('components.collection.edit.modal.title')}
            >
                <InputField
                    label={translate('components.collection.edit.modal.label')}
                    value={name}
                    onChange={onChange}
                    isInvalid={isModified && (inputValidator.nameLength || inputValidator.nameRequired)}
                    helperText={isModified && isInvalid && validationMessage}
                    hasClearButton
                />
            </Modal>
        ),
        openCollectionEditModal: onOpen,
    };
};
